import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { facebookHandler, appleHandler, googleHandler } from 'utils/OAuthHandlers';
import { logLogInPageSignupButtonTapped } from '@alltrails/analytics/amplitudeFunctions';
import { validateUserForm } from 'utils/form_validaters/user_form_helper';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { loadRecaptcha, executeRecaptcha } from 'utils/recaptcha_helpers';
import { ProPageStrings, StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from 'utils/language_support_util';
import SectionDivider from 'components/shared/SectionDivider';
import OutlinedTextField from '../textFields/OutlinedTextField';
import AltLoginsForm from '../AltLoginsForm';
import Wrapper from '../Wrapper';
import FormButton from '../FormButton';
import * as baseStyles from '../../../styles/baseStyles.module.scss';
import * as formStyles from '../styles/styles.module.scss';
import * as buttonStyles from '../buttons/styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';

const { OR } = StringConstantsMixin.strings;

const {
  formText: { login, termsLink },
  loginText: { forgotPassword, noAccount, signUpFree }
} = ProPageStrings;

const LOGIN_URL = '/api/alltrails/login';
const FORGOT_PASSWORD_URL = '/users/password/new';
const SIGN_UP_PAGE_URL = '/signup';

const TEXT = { ...ProPageStrings.loginText, ...ProPageStrings.loginForm, ...ProPageStrings.formText, ...ProPageStrings.altLoginsText };

const LoginForm = ({ enableRecaptcha, facebookAppId, handleLoginSuccess, handleRegisterClick, locale, returnTo = '/' }) => {
  const [serverError, setServerError] = useState();
  const [disableSubmit, setDisableSubmit] = useState(enableRecaptcha);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({ userEmail: '', userPassword: '' });
  const [recaptchaObject, setRecaptchaObject] = useState();
  const [showErrorsOnType, setShowErrorsOnType] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');

  useEffect(() => {
    if (enableRecaptcha) {
      loadRecaptcha(recaptchaObject => {
        setDisableSubmit(false);
        setRecaptchaObject(recaptchaObject);
      });
    }
  }, [enableRecaptcha]);

  function failedFormValidation() {
    let validationErrors = { ...errors };
    let validation;

    validation = validateUserForm('userEmail', userEmail, validationErrors);
    validationErrors = { ...validationErrors, ...validation.errors };

    validation = validateUserForm('userPassword', userPassword, validationErrors);
    validationErrors = { ...validationErrors, ...validation.errors };

    setErrors(validationErrors);

    return Object.keys(validationErrors).some(error => validationErrors[error].length > 0);
  }

  function loginSuccessCallback(data) {
    if (handleLoginSuccess) {
      return handleLoginSuccess(data);
    }

    window.location.href = data.welcome_notice ? `${returnTo + (returnTo.includes('?') ? '&' : '?')}welcome=${data.welcome_notice}` : returnTo;
  }

  function loginErrorCallback(err) {
    if (err && err.responseJSON && err.responseJSON.errors) {
      setServerError(err.responseJSON.errors[0].message);
    } else {
      setServerError(StringConstantsMixin.strings.ERR_TRY_AGAIN);
    }

    setSubmitting(false);
  }

  const loginCompleteCallback = () => {};

  function submitLogin(recaptchaToken) {
    const headers = locale ? { 'X-Language-Locale': locale } : {};
    const params = {
      create_session: true,
      email: userEmail,
      password: userPassword,
      recaptcha: recaptchaToken
    };

    ServerCommunicationUtil.postApiEndpoint(LOGIN_URL, params, loginSuccessCallback, loginErrorCallback, loginCompleteCallback, headers);
  }

  function handleLogin(e) {
    e.preventDefault();

    if (disableSubmit || submitting) {
      return;
    }

    if (failedFormValidation()) {
      setShowErrorsOnType(true);
      return;
    }

    setSubmitting(true);
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (recaptchaObject) {
      executeRecaptcha(recaptchaObject, 'userSignupLogin').then(
        recaptchaToken => submitLogin(recaptchaToken),
        () => setSubmitting(false)
      );
    } else {
      submitLogin();
    }
  }

  function handleChange(e) {
    e.preventDefault();
    const { name, value } = e.target;

    // we don't want to show errors on type unless a user has already tried to submit a form
    if (showErrorsOnType) {
      setErrors({ ...validateUserForm(name, value, { ...errors }) });
    }
  }

  function handleGoogle(response) {
    googleHandler(response, locale, loginSuccessCallback, () => {}, loginCompleteCallback);
  }

  function handleFacebook(response) {
    facebookHandler(response, locale, loginSuccessCallback, () => {}, loginCompleteCallback);
  }

  function renderServerError() {
    if (!serverError) {
      return null;
    }

    return <div className={formStyles.serverError}>{serverError}</div>;
  }

  function renderSubmitBtn() {
    const disabled = disableSubmit || submitting;
    return <FormButton value={login} disabled={disabled} />;
  }

  function renderForm() {
    return (
      <form id="login-form" onSubmit={handleLogin}>
        <div className={formStyles.inputItem}>
          <OutlinedTextField
            name="userEmail"
            errorMessage={errors.userEmail}
            value={userEmail}
            label={TEXT.email.title}
            changeHandler={e => {
              handleChange(e);
              setUserEmail(e.target.value);
            }}
            enterKeyHandler={handleLogin}
            type="email"
          />
        </div>
        <div className={`${formStyles.inputItem}`}>
          <OutlinedTextField
            name="userPassword"
            errorMessage={errors.userPassword}
            value={userPassword}
            label={TEXT.password.title}
            changeHandler={e => {
              handleChange(e);
              setUserPassword(e.target.value);
            }}
            enterKeyHandler={handleLogin}
            type="password"
          />
        </div>
        {renderServerError()}
        {renderSubmitBtn()}
      </form>
    );
  }

  function renderFooter() {
    const href = LanguageSupportUtil.wrapUrlSafe(SIGN_UP_PAGE_URL, locale);

    return (
      <>
        <div>
          {TEXT.privacyPolicyLead}{' '}
          <a href="/terms" className={baseStyles.route}>
            {termsLink}
          </a>{' '}
          {TEXT.and}{' '}
          <a href="/privacy" className={baseStyles.route}>
            {TEXT.privacy}
          </a>
          .
        </div>
        <div className={`${baseStyles.action}`}>
          {noAccount}{' '}
          <a
            className={baseStyles.route}
            onClick={e => {
              logLogInPageSignupButtonTapped();

              if (handleRegisterClick) {
                return handleRegisterClick(e);
              }

              return true;
            }}
            href={href}
          >
            {signUpFree}
          </a>
        </div>
      </>
    );
  }

  return (
    <Wrapper text={TEXT} foot={renderFooter()}>
      <div className={baseStyles.section}>
        {renderForm()}
        <div className={`${formStyles.action} ${styles.forgotPassword}`}>
          <a className={`${buttonStyles.text}`} href={LanguageSupportUtil.wrapUrlSafe(FORGOT_PASSWORD_URL, locale)}>
            {forgotPassword}
          </a>
        </div>
      </div>
      <SectionDivider classes={styles.sectionDivider}>{OR}</SectionDivider>
      <AltLoginsForm
        facebookAppId={facebookAppId}
        googleLoginHandler={handleGoogle}
        facebookLoginHandler={handleFacebook}
        appleLoginHandler={() => appleHandler()}
      />
    </Wrapper>
  );
};

LoginForm.propTypes = {
  enableRecaptcha: PropTypes.bool.isRequired,
  facebookAppId: PropTypes.number.isRequired,
  handleLoginSuccess: PropTypes.func,
  handleRegisterClick: PropTypes.func,
  locale: PropTypes.string.isRequired,
  returnTo: PropTypes.string // url
};

LoginForm.defaultProps = {
  handleLoginSuccess: null,
  handleRegisterClick: null,
  returnTo: '/'
};

export default LoginForm;
